import React, { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useLocation } from 'react-router-dom';

import {
  Box,
  Container,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Link,
  Stack,
  FormControlLabel,
  Switch,
  Typography,
  IconButton,
  InputAdornment,
} from '@mui/material';
import axios from 'axios';

import { useLoginStore } from '../../stores/AuthStatusStore';
import checkBlacklist from '../../utils/checkBlacklist';
import PasswordInput from '../PasswordInput';
import CadastroUsuario, { maskCpfCnpj } from './CadastroUsuario';
import RecuperarSenha from './RecuperarSenha';
import RedefinirSenha from './RedefinirSenha';

interface LoginUsuarioProps {
  tokenRecuperacao?: string | null;
}

const ROTAS_SEM_CADASTRO = ['/arquivosbrb', '/blacklist'];

const LoginUsuario: React.FC<LoginUsuarioProps> = ({ tokenRecuperacao }) => {
  const [recaptchaValido, setRecaptchaValido] = useState(false);
  const captchaRef = useRef<ReCAPTCHA>(null);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [login, setLogin] = useState<string>('');
  const [senha, setSenha] = useState<string>('');
  const [error, setError] = useState<string>('');
  const setLogado = useLoginStore(x => x.setLogin);
  const [loading, setLoading] = useState<boolean>(false);
  const [envioCorretor, setEnvioCorretor] = useState<boolean>(false);

  const [openCadastro, setOpenCadastro] = useState(false);
  const [openRecuperar, setOpenRecuperar] = useState(false);
  const [openRedefinir, setOpenRedefinir] = useState(false);
  const location = useLocation();

  const currentPath = location.pathname.toLowerCase().trim();
  const ocultarCadastro = ROTAS_SEM_CADASTRO.includes(currentPath);

  // const isVerificaCredenciamento = location.pathname.endsWith(
  //   'verifica-credenciamento',
  // );

  const [mostrarSenha, setMostrarSenha] = useState(false);

  const handleClickMostrarSenha = () => {
    setMostrarSenha(!mostrarSenha);
  };

  // Evita que o clique no ícone tire o foco do input
  const handleMouseDownPassword = event => {
    event.preventDefault();
  };

  useEffect(() => {
    if (tokenRecuperacao) {
      setOpenRedefinir(true);
    }
  }, [tokenRecuperacao]);

  useEffect(() => {
    setShowCaptcha(false);
    const timer = setTimeout(() => {
      setShowCaptcha(true);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const onCaptchaVerify = (token: string | null) => {
    setRecaptchaValido(!!token);
  };

  const handleLogin = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!recaptchaValido) {
      alert('Por favor, complete o reCAPTCHA.');
      return;
    }
    setLoading(true);
    const urlBase = process.env.REACT_APP_URL_BASE;
    const urlApi = process.env.REACT_APP_OG_API_URL;
    const Token = process.env.REACT_APP_TOKEN_S4E_V2;
    const TokenCorretor = '2FOPQWEUIRNPGUIQNAWEUIBGRUIQ3489GUBWVN4%@#@uyghin=';
    const cpfCleaned = login.replace(/[^\d]+/g, '');

    const endpointS4E = envioCorretor
      ? `${urlBase}/SYS/services/LoginService.aspx/Corretor`
      : `${urlBase}/SYS/services/LoginService.aspx/Colaborador`;

    const { isBlacklisted, message } = await checkBlacklist(cpfCleaned);
    if (isBlacklisted) {
      setLoading(false);
      setError(message);
      return;
    }

    let bodyS4E = {};
    if (envioCorretor) {
      bodyS4E = {
        token: TokenCorretor,
        cpfcnpj: cpfCleaned,
        senha: senha,
        tipoIdentificacao: 0,
      };
    } else {
      bodyS4E = {
        cpf: cpfCleaned,
        senha: senha,
        token: Token,
      };
    }

    try {
      const isVerificaCredenciamento = location.pathname.endsWith(
        'verifica-credenciamento',
      );

      if (isVerificaCredenciamento) {
        const responseS4E: any = await axios.post(endpointS4E, bodyS4E);

        if (responseS4E.data.codigo === 1) {
          setLogado(true);
          setTimeout(() => {
            alert('Sua sessão expirou. Faça login novamente.');
            setLogado(false);
          }, 30 * 60 * 1000);
          return;
        }
      }

      if (!envioCorretor) {
        const urlApiSecundaria = `${urlApi}/usuario-externo/login`;
        const acesso = location.pathname.split('/')[1].toLowerCase().trim();
        const bodyApi = {
          cpfCnpj: cpfCleaned,
          password: senha,
          acesso,
        };
        const responseAPI = await axios.post(urlApiSecundaria, bodyApi);

        if (responseAPI.status === 200) {
          sessionStorage.setItem(
            'usuarioLogado',
            JSON.stringify(responseAPI.data),
          );
          setLogado(true);
          return;
        } else {
          setLogado(false);
        }
      }

      setError('Login ou senha inválidos');
    } catch (e: any) {
      console.log(e.response);
      
      setError(e.response?.data || 'Erro de conexão ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setLogin(maskCpfCnpj(e.target.value));
    setError('');
  };

  const handleSenhaChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSenha(e.target.value);
    setError('');
  };

  return (
    <Container>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        padding="2rem"
      >
        <Paper
          elevation={3}
          style={{ padding: '2rem', maxWidth: '700px', width: '100%' }}
        >
          <form onSubmit={handleLogin}>
            {/* {!isVerificaCredenciamento && (
              <Box display="flex" justifyContent="center" mb={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    color={!envioCorretor ? 'primary' : 'textSecondary'}
                    fontWeight={!envioCorretor ? 'bold' : 'normal'}
                  >
                    Colaborador
                  </Typography>
                  <Switch
                    checked={envioCorretor}
                    onChange={() => setEnvioCorretor(!envioCorretor)}
                    color="primary"
                  />
                  <Typography
                    color={envioCorretor ? 'primary' : 'textSecondary'}
                    fontWeight={envioCorretor ? 'bold' : 'normal'}
                  >
                    Corretor
                  </Typography>
                </Stack>
              </Box>
            )} */}

            <TextField
              fullWidth
              margin="normal"
              label="Login"
              variant="outlined"
              value={login}
              onChange={handleLoginChange}
              error={!!error}
              autoComplete="new-password"
              required
            />

            <PasswordInput
              fullWidth
              margin="normal"
              value={senha}
              onChange={handleSenhaChange}
              error={!!error}
              helperText={error}
              required
            />

            <Box
              my={2}
              display="flex"
              justifyContent="center"
              sx={{ minHeight: '78px' }}
            >
              {showCaptcha && (
                <ReCAPTCHA
                  key="captcha-login-unique"
                  ref={captchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
                  onChange={onCaptchaVerify}
                />
              )}
            </Box>

            <Stack spacing={2} mt={3}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'LOGIN'
                )}
              </Button>

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={1}
              >
                <Link
                  component="button"
                  variant="body2"
                  onClick={e => {
                    e.preventDefault();
                    setOpenRecuperar(true);
                  }}
                  underline="hover"
                >
                  Esqueci minha senha
                </Link>

                {!ocultarCadastro && (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={e => {
                      e.preventDefault();
                      setOpenCadastro(true);
                    }}
                    underline="hover"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Cadastrar novo usuário
                  </Link>
                )}
              </Box>
            </Stack>
          </form>
        </Paper>
      </Box>

      {/* Modais mantidos do Principal */}
      <CadastroUsuario
        open={openCadastro}
        onClose={() => setOpenCadastro(false)}
      />

      <RecuperarSenha
        open={openRecuperar}
        onClose={() => setOpenRecuperar(false)}
        onTokenSent={() => {
          setOpenRecuperar(false);
          setOpenRedefinir(true);
        }}
      />

      <RedefinirSenha
        open={openRedefinir}
        onClose={() => setOpenRedefinir(false)}
        tokenRecuperacao={tokenRecuperacao}
      />
    </Container>
  );
};

export default LoginUsuario;
